import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { MeetingAgendaTemplate, TemplateType } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-template";
import { Team as TeamEntity } from "@common/ADAPT.Common.Model/organisation/team";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { lastValueFrom } from "rxjs";
import { IMeetingAgendaDetail, MeetingAgendaType, MeetingsService } from "../meetings.service";

@Component({
    selector: "adapt-template-selector",
    templateUrl: "./template-selector.component.html",
    standalone: false,
})
export class TemplateSelectorComponent extends BaseComponent implements OnChanges {
    public readonly MeetingAgendaType = MeetingAgendaType;

    @Input() public currentTeam?: TeamEntity;
    @Input() public selectedTemplateId?: number;
    @Input() public newAgendaTemplate?: MeetingAgendaTemplate;
    @Output() public selectedAgendaTemplateChange = new EventEmitter<IMeetingAgendaDetail>();

    public globalTemplates: IMeetingAgendaDetail[] = [];
    public teamAgendaTemplates: IMeetingAgendaDetail[] = [];
    public pathwayTemplates: IMeetingAgendaDetail[] = [];
    public otherTeamAgendaTemplates = new Map<TeamEntity, IMeetingAgendaDetail[]>();
    public selectedAgendaTemplate?: IMeetingAgendaDetail;
    private allAgendaTemplates: IMeetingAgendaDetail[] = [];

    public loading = true;
    public otherTeamsCollapsed = true;
    public pathwaysCollapsed = true;

    public constructor(
        elementRef: ElementRef,
        private meetingsService: MeetingsService,
    ) {
        super(elementRef);
    }

    public async ngOnChanges(changes: SimpleChanges) {
        if (changes.currentTeam || changes.newAgendaTemplate) {
            await this.updateTemplates();
            if (changes.newAgendaTemplate && this.newAgendaTemplate) {
                // select the newly added template by default
                const agendaDetails = this.teamAgendaTemplates.find((details) => details.meetingTemplate === this.newAgendaTemplate);
                if (agendaDetails) {
                    this.onAgendaSelected(agendaDetails);
                }
            }
        }

        if (changes.selectedTemplateId) {
            // find the selected template from all the templates
            const foundTemplate = this.allAgendaTemplates.find(({ meetingTemplate: template }) => template.meetingAgendaTemplateId === this.selectedTemplateId);
            if (foundTemplate) {
                this.onAgendaSelected(foundTemplate);

                // template is in another team, so auto-open the collapse (org is always expanded anyway)
                if (foundTemplate.meetingTemplate.team && foundTemplate.meetingTemplate.team !== this.currentTeam) {
                    this.otherTeamsCollapsed = false;
                }

                // template has an article, probably a pathway
                if (foundTemplate.meetingTemplate.meetingDescriptionArticleSlug) {
                    this.pathwaysCollapsed = false;
                }
            }
        }
    }

    public onAgendaSelected(agenda: IMeetingAgendaDetail) {
        this.selectedAgendaTemplate = agenda;
        this.selectedAgendaTemplateChange.emit(agenda);
    }

    private async updateTemplates() {
        this.otherTeamAgendaTemplates.clear();
        const teamAgendaTemplates: IMeetingAgendaDetail[] = [];
        const globalTemplates: IMeetingAgendaDetail[] = [];
        const pathwayTemplates: IMeetingAgendaDetail[] = [];
        const allAgendaTemplates: IMeetingAgendaDetail[] = [];

        let allTemplates = await lastValueFrom(this.meetingsService.getMeetingAgendaTemplates());
        allTemplates = allTemplates.filter((template) => !template.entityAspect.entityState.isAddedModifiedOrDeleted());
        for (const meetingTemplate of allTemplates) {
            const isTeam = !!meetingTemplate.teamId;
            const isSystem = !!meetingTemplate.code;
            const isPathway = this.isAlto && !!meetingTemplate.meetingDescriptionArticleSlug;

            let type = MeetingAgendaType.Organisation;
            if (isSystem) {
                type = meetingTemplate.templateType === TemplateType.SystemCoachOnly
                    ? MeetingAgendaType.SystemCoachOnly
                    : MeetingAgendaType.System;
            } else if (isTeam) {
                type = MeetingAgendaType.Team;
            }

            const detail = { meetingTemplate, type };

            if (isPathway) {
                pathwayTemplates.push(detail);
            } else if (meetingTemplate.team) {
                if (meetingTemplate.team === this.currentTeam) {
                    teamAgendaTemplates.push(detail);
                } else {
                    const teamItems = this.otherTeamAgendaTemplates.get(meetingTemplate.team) ?? [];
                    teamItems.push(detail);
                    this.otherTeamAgendaTemplates.set(meetingTemplate.team, teamItems);
                }
            } else {
                globalTemplates.push(detail);
            }

            allAgendaTemplates.push(detail);
        }

        this.globalTemplates = globalTemplates.sort(this.meetingsService.sortTemplates);
        this.teamAgendaTemplates = teamAgendaTemplates.sort(this.meetingsService.sortTemplates);
        this.pathwayTemplates = pathwayTemplates.sort(this.meetingsService.sortTemplates);
        for (const teamTemplates of this.otherTeamAgendaTemplates.values()) {
            teamTemplates.sort(this.meetingsService.sortTemplates);
        }

        // doesn't need to be sorted as order doesn't matter
        this.allAgendaTemplates = allAgendaTemplates;

        this.loading = false;
    }
}
